import {
  CATEGORIES,
  SETTLEMENT_EMOJI,
  formatMoney as formatMoneyShared,
  type Money,
  type Transaction,
} from '@debtbot/shared';
import { getLocale, t as dict } from '../i18n';

export function formatMoney(
  money: Money,
  opts: Parameters<typeof formatMoneyShared>[1] = {},
): string {
  return formatMoneyShared(money, { locale: getLocale(), ...opts });
}

export function fullName(u: { first_name: string; last_name: string | null }): string {
  return [u.first_name, u.last_name].filter(Boolean).join(' ');
}

/** «7 окт», «7 окт 2025» — всегда дата, без «сегодня»/«вчера». */
export function formatDay(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const base = `${d.getDate()} ${dict().date.months[d.getMonth()]}`;
  return d.getFullYear() === now.getFullYear() ? base : `${base} ${d.getFullYear()}`;
}

/** «сегодня», «вчера», «7 окт», «7 окт 2025» — по таймзоне устройства. */
export function formatRelativeDay(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86_400_000);
  if (diffDays === 0) return dict().date.today;
  if (diffDays === 1) return dict().date.yesterday;
  return formatDay(iso, now);
}

export function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${formatRelativeDay(iso)}, ${hh}:${mm}`;
}

export function balanceTone(amount: number): 'positive' | 'negative' | 'zero' {
  return amount > 0 ? 'positive' : amount < 0 ? 'negative' : 'zero';
}

export function primaryBalance(balances: Money[]): Money {
  return balances[0] ?? { amount: 0, currency: 'UZS' };
}

export function txEmoji(t: Pick<Transaction, 'type' | 'category'>): string {
  if (t.type === 'SETTLEMENT') return SETTLEMENT_EMOJI;
  return t.category ? CATEGORIES[t.category].emoji : CATEGORIES.other.emoji;
}

export function txTitle(t: Pick<Transaction, 'type' | 'category' | 'note'>): string {
  if (t.note) return t.note;
  if (t.type === 'SETTLEMENT') return dict().txKind.settlement;
  return t.category ? CATEGORIES[t.category].label[getLocale()] : dict().txKind.expense;
}

/** Знак операции с точки зрения me: +amount если платил я (peer должен больше). */
export function txDelta(
  t: Pick<Transaction, 'payer_id' | 'amount' | 'currency'>,
  meId: string,
): Money {
  return { amount: t.payer_id === meId ? t.amount : -t.amount, currency: t.currency };
}

export function groupByDay<T extends { occurred_at: string }>(
  items: T[],
): { label: string; items: T[] }[] {
  const groups: { label: string; items: T[] }[] = [];
  for (const item of items) {
    const label = capitalize(formatRelativeDay(item.occurred_at));
    const last = groups.at(-1);
    if (last && last.label === label) last.items.push(item);
    else groups.push({ label, items: [item] });
  }
  return groups;
}
